<template>
  <basic-page
    :title="cateringEvent.description || 'Muonitus'"
    :prev="true"
    :fallback="{ name: 'Muonitus' }"
  >
    <template v-slot:app-bar-buttons>
      <div v-if="saving" class="mr-4 caption grey--text d-flex align-center">
        <v-progress-circular
          indeterminate
          size="16"
          width="2"
          class="mr-2"
        ></v-progress-circular>
        Tallennetaan...
      </div>
      <v-btn icon title="Kopioi linkki" @click="copyPageUrl">
        <v-icon>mdi-link</v-icon>
      </v-btn>
      <v-btn icon title="Tulosta ohje" @click="printPdf">
        <v-icon>mdi-printer</v-icon>
      </v-btn>
      <v-btn icon title="Tallenna" @click="saveEvent(true)">
        <v-icon>mdi-content-save</v-icon>
      </v-btn>
    </template>
    <v-row class="fill-height muonitus-tapahtuma">
      <v-col cols="12" lg="10" offset-lg="1" xl="8" offset-xl="2">
        <muonitus-ohje />

        <div class="d-flex flex-wrap justify-space-around mb-4">
          <div
            v-if="cateringEvent.kitchenLeader"
            class="text-h6 text-md-h5 font-weight-bold primary--text text-center mx-4"
          >
            <v-icon size="36" color="primary" class="mr-2">mdi-chef-hat</v-icon>
            Keittiö: {{ cateringEvent.kitchenLeader }}
            <a
              v-if="getPersonPhone(cateringEvent.kitchenLeader)"
              :href="'tel:' + getPersonPhone(cateringEvent.kitchenLeader)"
              class="text-subtitle-1 text-decoration-none"
            >
              <v-icon small color="primary">mdi-phone</v-icon>
              {{ getPersonPhone(cateringEvent.kitchenLeader) }}
            </a>
          </div>
          <div
            v-if="cateringEvent.fieldLeader"
            class="text-h6 text-md-h5 font-weight-bold success--text text-center mx-4"
          >
            <v-icon size="36" color="success" class="mr-2"
              >mdi-clipboard-account</v-icon
            >
            Kenttä: {{ cateringEvent.fieldLeader }}
            <a
              v-if="getPersonPhone(cateringEvent.fieldLeader)"
              :href="'tel:' + getPersonPhone(cateringEvent.fieldLeader)"
              class="text-subtitle-1 text-decoration-none"
            >
              <v-icon small color="success">mdi-phone</v-icon>
              {{ getPersonPhone(cateringEvent.fieldLeader) }}
            </a>
          </div>
        </div>

        <v-tabs v-model="activeTab" class="mb-4">
          <v-tab><v-icon left>mdi-pencil-ruler</v-icon> Suunnittelu</v-tab>
          <v-tab><v-icon left>mdi-clipboard-list</v-icon> Toteutus</v-tab>
          <v-tab><v-icon left>mdi-finance</v-icon> Talous</v-tab>
        </v-tabs>

        <v-tabs-items
          v-model="activeTab"
          style="background-color: transparent"
          touchless
        >
          <!-- TAB 1: SUUNNITTELU -->
          <v-tab-item>
            <v-row>
              <!-- Left Column -->
              <v-col cols="12" md="6">
                <v-card class="mb-4" elevation="2" rounded="lg">
                  <v-card-title class="primary--text">
                    <v-icon left color="primary">mdi-calendar-clock</v-icon>
                    Ajankohta ja Tila
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-menu
                          v-model="dateMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="formattedDate"
                              label="Pvm (Alku)"
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-2"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="datePickerDate"
                            first-day-of-week="1"
                            @input="updateDate"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="timePickerTime"
                          label="Klo (Alku)"
                          type="time"
                          @change="updateTime"
                          outlined
                          dense
                          hide-details="auto"
                          class="mb-2"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-menu
                          v-model="endDateMenu"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="formattedEndDate"
                              label="Pvm (Loppu)"
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-2"
                              clearable
                              @click:clear="
                                endDatePickerDate = null;
                                updateEndTime();
                              "
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="endDatePickerDate"
                            first-day-of-week="1"
                            @input="updateEndDate"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="timePickerEndTime"
                          label="Klo (Loppu)"
                          type="time"
                          @change="updateEndTime"
                          outlined
                          dense
                          hide-details="auto"
                          class="mb-2"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-select
                      v-model="cateringEvent.status"
                      :items="statusOptions"
                      label="Tila"
                      outlined
                      dense
                      hide-details="auto"
                      class="mb-2"
                    ></v-select>

                    <v-select
                      v-model="cateringEvent.type"
                      :items="typeOptions"
                      label="Tyyppi"
                      @change="updateDefaultPrice"
                      :hint="selectedTypeDescription"
                      persistent-hint
                      outlined
                      dense
                    ></v-select>

                    <v-checkbox
                      v-model="cateringEvent.secappSent"
                      label="Secapp lähetetty"
                      :class="{ 'success--text': cateringEvent.secappSent }"
                      dense
                      hide-details
                      class="mt-0"
                    >
                      <template v-slot:label>
                        <span
                          :class="{
                            'success--text font-weight-bold':
                              cateringEvent.secappSent,
                          }"
                          >Secapp lähetetty</span
                        >
                      </template>
                    </v-checkbox>
                  </v-card-text>
                </v-card>

                <v-card class="mb-4" elevation="2" rounded="lg">
                  <v-card-title class="primary--text">
                    <v-icon left color="primary">mdi-information</v-icon>
                    Perustiedot
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-text-field
                      v-model="cateringEvent.description"
                      label="Kuvaus / Nimi"
                      outlined
                      dense
                    ></v-text-field>
                    <v-text-field
                      v-model="cateringEvent.siteId"
                      label="Kohde / Osoite"
                      outlined
                      dense
                    ></v-text-field>
                    <v-select
                      v-model="cateringEvent.payer"
                      :items="payerOptions"
                      label="Tilaaja / Maksaja"
                      @change="checkOrderReference"
                      outlined
                      dense
                    ></v-select>

                    <v-alert
                      v-if="cateringEvent.payer === 'Pelastuslaitos'"
                      border="left"
                      colored-border
                      color="info"
                      elevation="1"
                      class="mb-4"
                    >
                      <div class="subtitle-2 font-weight-bold mb-1">
                        Yhteystiedot (Pelastuslaitos):
                      </div>
                      <div>
                        <strong>P511:</strong>
                        <a
                          href="tel:+358440983181"
                          class="text-decoration-none"
                        >
                          +358 44 098 3181
                        </a>
                      </div>
                      <div>
                        <strong>Tilannekeskus:</strong>
                        <a href="tel:+35826211500" class="text-decoration-none">
                          +358 26 211 500
                        </a>
                      </div>
                      <div>
                        <strong>P31:</strong>
                        <a
                          href="tel:+358447011503"
                          class="text-decoration-none"
                        >
                          +358 44 701 1503
                        </a>
                      </div>
                      <div>
                        <strong>P32:</strong>
                        <a
                          href="tel:+358447017353"
                          class="text-decoration-none"
                        >
                          +358 44 701 7353
                        </a>
                      </div>
                      <div>
                        <strong>P33:</strong>
                        <a
                          href="tel:+358447017462"
                          class="text-decoration-none"
                        >
                          +358 44 701 7462
                        </a>
                      </div>
                    </v-alert>

                    <div
                      v-if="cateringEvent.payer === 'Oma osasto'"
                      class="mb-4"
                    >
                      <div class="text-subtitle-2 mb-2">
                        Kustannusten kohdistus:
                      </div>
                      <v-row dense>
                        <v-col
                          cols="12"
                          sm="6"
                          v-for="(val, key) in costAllocationOptions"
                          :key="key"
                        >
                          <v-checkbox
                            v-model="cateringEvent.costAllocation[key]"
                            :label="val"
                            dense
                            hide-details
                            @change="saveEventDebounced"
                            class="mt-0"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </div>

                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="cateringEvent.subscriberContact"
                          label="Tilaajan yhteyshenkilö"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="cateringEvent.subscriberPhone"
                          label="Tilaajan yhteyshenkilön puhelinnumero"
                          outlined
                          dense
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-text-field
                      v-model="cateringEvent.orderReference"
                      label="Tilausviite"
                      outlined
                      dense
                    ></v-text-field>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Right Column -->
              <v-col cols="12" md="6">
                <v-card class="mb-4" elevation="2" rounded="lg">
                  <v-card-title class="primary--text">
                    <v-icon left color="primary">mdi-food</v-icon>
                    Muonitus
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-combobox
                      v-model="cateringEvent.contentDescription"
                      :items="menus"
                      item-text="name"
                      item-value="name"
                      :return-object="false"
                      label="Menun sisältö"
                      @change="onMenuChange"
                      outlined
                      dense
                    ></v-combobox>
                    <v-alert
                      v-if="selectedMenuDescription"
                      border="left"
                      colored-border
                      color="info"
                      elevation="1"
                      dense
                      class="mb-4"
                    >
                      <div class="text-caption">
                        {{ selectedMenuDescription }}
                      </div>
                    </v-alert>

                    <v-text-field
                      v-model.number="cateringEvent.headCountEstimate"
                      label="Tilattu hlö-määrä"
                      type="number"
                      @change="updateShoppingListAmounts"
                      outlined
                      dense
                      suffix="hlö"
                    ></v-text-field>

                    <v-textarea
                      v-model="cateringEvent.diets"
                      label="Huomioitavat ruokavaliot"
                      rows="3"
                      auto-grow
                      outlined
                      dense
                    ></v-textarea>
                  </v-card-text>
                </v-card>

                <v-card class="mb-4" elevation="2" rounded="lg">
                  <v-card-title class="primary--text">
                    <v-icon left color="primary">mdi-account-group</v-icon>
                    Henkilöstö
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-combobox
                      v-model="cateringEvent.kitchenLeader"
                      :items="supportPersonnel"
                      :item-text="getPersonDisplayName"
                      item-value="name"
                      :return-object="false"
                      label="Ryhmänjohtaja (Keittiö)"
                      outlined
                      dense
                    ></v-combobox>
                    <v-combobox
                      v-model="cateringEvent.fieldLeader"
                      :items="supportPersonnel"
                      :item-text="getPersonDisplayName"
                      item-value="name"
                      :return-object="false"
                      label="Ryhmänjohtaja (Kenttä)"
                      outlined
                      dense
                    ></v-combobox>

                    <div class="text-subtitle-2 mt-2 mb-1">
                      Muonitusryhmän jäsenet
                    </div>
                    <div
                      v-if="cateringEvent.legacyGroupMembers"
                      class="caption red--text mb-2"
                    >
                      Vanhat tiedot: {{ cateringEvent.legacyGroupMembers }}
                    </div>
                    <v-card outlined class="pa-2">
                      <v-row dense>
                        <v-col
                          cols="12"
                          sm="6"
                          v-for="person in supportPersonnel"
                          :key="person.id"
                        >
                          <v-checkbox
                            v-model="cateringEvent.groupMembers"
                            :label="getPersonDisplayName(person)"
                            :value="person.id"
                            dense
                            hide-details
                            class="mt-0"
                          ></v-checkbox>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 2: TOTEUTUS -->
          <v-tab-item>
            <v-row>
              <v-col cols="12" md="6">
                <muonitus-checklist
                  v-model="cateringEvent.checklist"
                  :packed-items.sync="cateringEvent.packedItems"
                  :items="checklistItems"
                  @input="saveEventDebounced"
                  @save="saveEventDebounced"
                  @share="shareShoppingList"
                />
              </v-col>
              <v-col cols="12" md="6">
                <muonitus-shopping-list
                  :shopping-list="cateringEvent.shoppingList"
                  :public-notes.sync="cateringEvent.publicNotes"
                  :categories="categories"
                  :event-id="cateringEvent.id"
                  :head-count="cateringEvent.headCountEstimate"
                  :templates="templates"
                  @save="saveEventDebounced"
                  @share="shareShoppingList"
                  @apply-template="applyTemplate"
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- TAB 3: TALOUS -->
          <v-tab-item>
            <v-row>
              <!-- Left Column: Costs & Receipts -->
              <v-col cols="12" md="6">
                <v-card class="mb-4" elevation="2" rounded="lg">
                  <v-card-title class="primary--text">
                    <v-icon left color="primary">mdi-receipt</v-icon>
                    Ostot ja Kuitit
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-text-field
                      v-model="cateringEvent.purchaseLocation"
                      label="Kauppa / Hankintapaikka"
                      outlined
                      dense
                    ></v-text-field>
                    <v-text-field
                      v-model.number="cateringEvent.receiptTotal"
                      label="Ostokuitin summa"
                      type="number"
                      outlined
                      dense
                      suffix="€"
                    ></v-text-field>

                    <v-divider class="my-4"></v-divider>
                    <div class="text-subtitle-2 mb-2">Kuitit</div>

                    <file-upload
                      ref="receiptUpload"
                      :multiple="true"
                      :max-files="5"
                      accepted-file-types="image/*"
                      label="Raahaa kuitit tähän"
                    />
                    <v-btn
                      class="mt-2"
                      color="primary"
                      small
                      @click="uploadReceiptFromComponent"
                      :loading="uploadingReceipt"
                    >
                      <v-icon left>mdi-cloud-upload</v-icon> Lataa kuitit
                    </v-btn>

                    <div
                      v-if="
                        cateringEvent.receiptImageUrls &&
                        cateringEvent.receiptImageUrls.length > 0
                      "
                      class="d-flex flex-wrap mt-4"
                    >
                      <div
                        v-for="(url, index) in cateringEvent.receiptImageUrls"
                        :key="index"
                        class="d-flex flex-column align-center mr-4 mb-4"
                      >
                        <v-card outlined hover @click="selectedPicture = url">
                          <v-img
                            :src="url"
                            width="100"
                            height="100"
                            contain
                            class="grey lighten-4"
                          ></v-img>
                        </v-card>
                        <v-btn
                          icon
                          color="error"
                          x-small
                          class="mt-1"
                          @click="deleteReceipt(url)"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Right Column: Billing & Feedback -->
              <v-col cols="12" md="6">
                <v-card class="mb-4" elevation="2" rounded="lg">
                  <v-card-title class="primary--text">
                    <v-icon left color="primary">mdi-cash-register</v-icon>
                    Laskutusperusteet
                    <v-spacer></v-spacer>
                    <v-btn
                      icon
                      title="Kopioi laskutustiedot"
                      @click="copyInvoiceData"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-row>
                      <v-col cols="6">
                        <v-text-field
                          :value="cateringEvent.headCountEstimate"
                          label="Tilattu määrä"
                          readonly
                          outlined
                          dense
                          suffix="hlö"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model.number="cateringEvent.unitPrice"
                          label="Yksikköhinta"
                          type="number"
                          outlined
                          dense
                          suffix="€/hlö"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-text-field
                      v-model.number="cateringEvent.inventoryUsageValue"
                      label="Käyttö varastosta (esim. mukit)"
                      type="number"
                      outlined
                      dense
                      suffix="€"
                    ></v-text-field>

                    <v-card outlined class="pa-3 grey lighten-5 mb-4">
                      <div class="d-flex justify-space-between align-center">
                        <span class="text-subtitle-2">Laskutushinta:</span>
                        <span class="text-h6 primary--text"
                          >{{ billingTotal.toFixed(2) }} €</span
                        >
                      </div>
                      <div
                        class="d-flex justify-space-between align-center mt-1"
                      >
                        <span class="text-subtitle-2">Ostot:</span>
                        <span class="text-body-1"
                          >{{
                            (cateringEvent.receiptTotal || 0).toFixed(2)
                          }}
                          €</span
                        >
                      </div>
                      <div
                        class="d-flex justify-space-between align-center mt-1"
                      >
                        <span class="text-subtitle-2">Varasto:</span>
                        <span class="text-body-1"
                          >{{
                            (cateringEvent.inventoryUsageValue || 0).toFixed(2)
                          }}
                          €</span
                        >
                      </div>
                      <v-divider class="my-2"></v-divider>
                      <div class="d-flex justify-space-between align-center">
                        <span class="text-subtitle-2">Ylijäämä:</span>
                        <span
                          class="text-h6"
                          :class="
                            billingTotal -
                              ((cateringEvent.receiptTotal || 0) +
                                (cateringEvent.inventoryUsageValue || 0)) >=
                            0
                              ? 'success--text'
                              : 'error--text'
                          "
                        >
                          {{
                            (
                              billingTotal -
                              ((cateringEvent.receiptTotal || 0) +
                                (cateringEvent.inventoryUsageValue || 0))
                            ).toFixed(2)
                          }}
                          €
                        </span>
                      </div>
                    </v-card>
                  </v-card-text>
                </v-card>

                <v-card class="mb-4" elevation="2" rounded="lg">
                  <v-card-title class="primary--text">
                    <v-icon left color="primary">mdi-message-text</v-icon>
                    Palaute ja Logi
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text>
                    <v-textarea
                      v-model="cateringEvent.feedback"
                      label="Palaute / Jälkikirjaus"
                      rows="3"
                      auto-grow
                      outlined
                      dense
                      placeholder="Esim. ruokaa jäi yli, erityisjärjestelyt..."
                    ></v-textarea>

                    <v-text-field
                      v-model.number="cateringEvent.headCountReal"
                      label="Toteutunut hlö-määrä"
                      type="number"
                      outlined
                      dense
                      suffix="hlö"
                    ></v-text-field>

                    <v-text-field
                      v-model="cateringEvent.participantsInfo"
                      label="Osallistujatiedot (esim. yksiköt)"
                      outlined
                      dense
                    ></v-text-field>

                    <div
                      v-if="cateringEvent.createdAt || cateringEvent.updatedAt"
                      class="caption grey--text mt-4"
                    >
                      <div v-if="cateringEvent.createdBy">
                        Luonut:
                        {{
                          cateringEvent.createdBy.name ||
                          cateringEvent.createdBy.email
                        }}
                        ({{ formatDate(cateringEvent.createdAt) }})
                      </div>
                      <div v-if="cateringEvent.updatedBy">
                        Muokannut:
                        {{
                          cateringEvent.updatedBy.name ||
                          cateringEvent.updatedBy.email
                        }}
                        ({{ formatDate(cateringEvent.updatedAt) }})
                      </div>
                    </div>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn v-if="$isAdmin || $user.email === 'laura.laitila@icloud.com'" color="error" text @click="deleteEvent">
                      <v-icon left>mdi-delete</v-icon> Poista tapahtuma
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
    <picture-dialog v-model="selectedPicture" />
  </basic-page>
</template>

<script>
import { firestore as db, storage } from "@/plugins/firebase.app";
import {
  doc,
  onSnapshot,
  updateDoc,
  collection,
  getDocs,
  getDoc,
  Timestamp,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { debounce } from "lodash";
import PictureDialog from "@/views/PictureDialog.vue";
import BasicPage from "@/components/BasicPage.vue";
import FileUpload from "@/components/FileUpload.vue";
import MuonitusOhje from "@/components/MuonitusOhje.vue";
import MuonitusChecklist from "@/components/MuonitusChecklist.vue";
import MuonitusShoppingList from "@/components/MuonitusShoppingList.vue";
import dayjs from "dayjs";
import { generateMuonitusPdf } from "@/utils/muonitusPdf";

export default {
  name: "MuonitusTapahtuma",
  components: {
    PictureDialog,
    BasicPage,
    FileUpload,
    MuonitusOhje,
    MuonitusChecklist,
    MuonitusShoppingList,
  },
  data() {
    return {
      activeTab: 0,
      cateringEvent: {
        id: this.$route.params.id,
        description: "",
        headCountEstimate: 0,
        headCountReal: 0,
        status: "planned",
        shoppingList: [],
        receiptTotal: 0,
        diets: "",
        feedback: "",
        participantsInfo: "",
        receiptImageUrl: null, // Deprecated, kept for backward compatibility
        receiptImageUrls: [],
        type: null,
        contentDescription: "",
        payer: null,
        unitPrice: 6.0,
        inventoryUsageValue: 0,
        date: null,
        subscriberContact: "",
        siteId: "",
        purchaseLocation: "",
        orderReference: "",
        createdBy: null,
        updatedBy: null,
        createdAt: null,
        updatedAt: null,
        checklist: {},
        packedItems: {},
        costAllocation: {
          halytysosasto: false,
          nuorisoOsasto: false,
          varhaisnuorisoOsasto: false,
          naisosasto: false,
          veteraaniOsasto: false,
          yhdistystoiminta: false,
          leirikoski: false,
        },
        publicAccess: true,
        kitchenLeader: "",
        fieldLeader: "",
        groupMembers: [], // Changed from string to array
      },
      templates: [],
      menus: [],
      supportPersonnel: [],
      statusOptions: [
        { text: "Suunnitteilla", value: "planned" },
        { text: "Ostoksilla / Valmistuksessa", value: "shopping" },
        { text: "Toimitettu maastoon", value: "delivered" },
        { text: "Valmis / Purettu", value: "done" },
        { text: "Laskutettu", value: "billed" },
      ],
      eventTypes: [], // Store full cateringEvent type objects
      typeOptions: [], // Mapped for v-select
      payerOptions: ["Pelastuslaitos", "Oma osasto", "Ulkopuolinen"],
      costAllocationOptions: {
        halytysosasto: "Hälytysosasto",
        nuorisoOsasto: "Nuoriso-osasto",
        varhaisnuorisoOsasto: "Varhaisnuoriso-osasto",
        naisosasto: "Tukiosasto",
        veteraaniOsasto: "Veteraaniosasto",
        yhdistystoiminta: "Yhdistystoiminta",
        leirikoski: "Leirikoski",
      },
      categories: [], // Fetched from settings
      unsubscribe: null,
      uploadingReceipt: false,
      selectedPicture: null,
      dateMenu: false,
      datePickerDate: null,
      timePickerTime: null,
      endDateMenu: false,
      endDatePickerDate: null,
      timePickerEndTime: null,
      saving: false,
      checklistItems: [],
    };
  },
  computed: {
    billingTotal() {
      return (
        (this.cateringEvent.headCountEstimate || 0) *
        (this.cateringEvent.unitPrice || 0)
      );
    },
    formattedDate() {
      if (this.datePickerDate) {
        return dayjs(this.datePickerDate).format("DD.MM.YYYY");
      }
      if (!this.cateringEvent.date) return "";
      return dayjs(this.cateringEvent.date.toDate()).format("DD.MM.YYYY");
    },
    formattedEndDate() {
      if (this.endDatePickerDate) {
        return dayjs(this.endDatePickerDate).format("DD.MM.YYYY");
      }
      if (this.cateringEvent.endDate) {
        return dayjs(this.cateringEvent.endDate.toDate()).format("DD.MM.YYYY");
      }
      if (this.datePickerDate) {
        return dayjs(this.datePickerDate).format("DD.MM.YYYY");
      }
      return "";
    },
    selectedTypeDescription() {
      const type = this.eventTypes.find(
        (t) => t.id === this.cateringEvent.type,
      );
      if (type) {
        return `${type.description || ""} (${type.price} €)`;
      }
      return "";
    },
    selectedMenuDescription() {
      if (!this.cateringEvent.contentDescription) return "";
      const menu = this.menus.find(
        (m) => m.name === this.cateringEvent.contentDescription,
      );
      return menu ? menu.description : "";
    },
    selectedCostAllocations() {
      if (!this.cateringEvent.costAllocation) return [];
      return Object.keys(this.cateringEvent.costAllocation)
        .filter((key) => this.cateringEvent.costAllocation[key])
        .map((key) => this.costAllocationOptions[key] || key);
    },
  },
  async created() {
    // Fetch settings
    const settingsRef = doc(db, "catering_settings", "general");
    const settingsSnap = await getDoc(settingsRef);
    if (settingsSnap.exists()) {
      const data = settingsSnap.data();
      this.categories = data.categories || [];
      this.eventTypes = data.eventTypes || [];
      this.checklistItems = data.checklistItems || [];
      this.menus = (data.menus || []).map((m) => {
        if (typeof m === "string") {
          return { name: m, description: "", templateId: null };
        }
        return m;
      });
      this.typeOptions = this.eventTypes.map((t) => ({
        text: t.name,
        value: t.id,
      }));
    } else {
      // Fallback defaults if settings not found
      this.categories = [
        "HeVi",
        "Leipä",
        "Kuivatuotteet",
        "Maitotuotteet",
        "Juomat",
        "Tarvikkeet",
        "Muu",
      ];
      this.typeOptions = [
        { text: "Hälytys (1. aalto / Kahvi)", value: "halytys_1" },
        { text: "Hälytys (2. aalto / Ruoka)", value: "halytys_2" },
        { text: "Harjoitus", value: "harjoitus" },
        { text: "Kurssi", value: "kurssi" },
        { text: "Kokous", value: "kokous" },
        { text: "Tapahtuma", value: "tapahtuma" },
        { text: "Muu", value: "muu" },
      ];
      // Construct eventTypes for fallback to support price lookup
      this.eventTypes = [
        {
          id: "halytys_1",
          price: 6.5,
          description: "Sisältää: Kahvi ja sämpylä",
        },
        {
          id: "halytys_2",
          price: 8.65,
          description: "Sisältää: Lounas (lämmin ruoka, juoma, kahvi)",
        },
        {
          id: "harjoitus",
          price: 6.5,
          description: "Sisältää: Kahvi ja sämpylä",
        },
        {
          id: "kurssi",
          price: 8.65,
          description: "Sisältää: Lounas (lämmin ruoka, juoma, kahvi)",
        },
        { id: "kokous", price: 4.3, description: "Sisältää: Kahvi ja pulla" },
        { id: "tapahtuma", price: 1.6, description: "Sisältää: Makkara" },
        { id: "muu", price: 0, description: "" },
      ];
    }

    const eventId = this.$route.params.id;
    this.unsubscribe = onSnapshot(doc(db, "caterings", eventId), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.deletedAt) {
          this.$toast("Muonitus on poistettu", { type: "info" });
          this.$router.push({ name: "Muonitus" });
          return;
        }

        this.cateringEvent = {
          id: doc.id,
          subscriberContact: "",
          siteId: "",
          orderReference: "",
          groupMembers: [],
          kitchenLeader: "",
          fieldLeader: "",
          ...data,
          unitPrice: data.unitPrice !== undefined ? data.unitPrice : 6.0,
          inventoryUsageValue:
            data.inventoryUsageValue !== undefined
              ? data.inventoryUsageValue
              : 0,
        };
        // Ensure groupMembers is an array
        if (typeof this.cateringEvent.groupMembers === "string") {
          // If it was a string, we might want to keep it or convert it.
          // For now, let's wrap it in an array if it's not empty, or split by comma?
          // Or just reset it if we want to enforce the new system.
          // Let's try to split by comma if it looks like a list, otherwise just wrap.
          if (this.cateringEvent.groupMembers) {
            // We can't easily map names to IDs, so we might just have to accept that old string data is "lost" to the new UI
            // or we display the old string in a separate field.
            // Let's just initialize to empty array if string to avoid type errors in v-select/checkboxes
            // But wait, if I save, I overwrite the old data.
            // Maybe I should add a legacy field.
            this.cateringEvent.legacyGroupMembers =
              this.cateringEvent.groupMembers;
            this.cateringEvent.groupMembers = [];
          } else {
            this.cateringEvent.groupMembers = [];
          }
        }
        if (!this.cateringEvent.shoppingList)
          this.cateringEvent.shoppingList = [];
        if (!this.cateringEvent.receiptImageUrls)
          this.cateringEvent.receiptImageUrls = [];
        if (!this.cateringEvent.costAllocation) {
          this.cateringEvent.costAllocation = {
            halytysosasto: false,
            nuorisoOsasto: false,
            varhaisnuorisoOsasto: false,
            naisosasto: false,
            veteraaniOsasto: false,
            yhdistystoiminta: false,
            leirikoski: false,
          };
        }
        if (!this.cateringEvent.checklist) {
          this.cateringEvent.checklist = {};
        }
        if (!this.cateringEvent.packedItems) {
          this.cateringEvent.packedItems = {};
        }

        // Apply defaults if checklist is empty (has no keys)
        if (
          Object.keys(this.cateringEvent.checklist).length === 0 &&
          this.checklistItems.length > 0
        ) {
          this.checklistItems.forEach((item) => {
            if (item.defaultChecked) {
              this.$set(this.cateringEvent.checklist, item.label, true);
            }
          });
        }

        // Migration for old single image
        if (
          this.cateringEvent.receiptImageUrl &&
          this.cateringEvent.receiptImageUrls.length === 0
        ) {
          this.cateringEvent.receiptImageUrls.push(
            this.cateringEvent.receiptImageUrl,
          );
        }

        if (this.cateringEvent.date) {
          const d = dayjs(this.cateringEvent.date.toDate());
          this.datePickerDate = d.format("YYYY-MM-DD");
          this.timePickerTime = d.format("HH:mm");
        }
        if (this.cateringEvent.endDate) {
          const d = dayjs(this.cateringEvent.endDate.toDate());
          const endDateStr = d.format("YYYY-MM-DD");
          this.timePickerEndTime = d.format("HH:mm");

          if (this.datePickerDate !== endDateStr) {
            this.endDatePickerDate = endDateStr;
          } else {
            this.endDatePickerDate = null;
          }
        }
      }
    });

    const templatesSnap = await getDocs(collection(db, "catering_templates"));
    this.templates = templatesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const personnelSnap = await getDocs(collection(db, "tukiosaston_henkilot"));
    this.supportPersonnel = personnelSnap.docs
      .map((d) => ({
        id: d.id,
        ...d.data(),
      }))
      .sort((a, b) => {
        const nameA = (a.name || "").toString().toLowerCase();
        const nameB = (b.name || "").toString().toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return "";
      return dayjs(timestamp.toDate()).format("DD.MM.YYYY HH:mm");
    },
    getPersonPhone(name) {
      if (!name) return "";
      const person = this.supportPersonnel.find((p) => p.name === name);
      return person && person.phone ? person.phone : "";
    },
    getPersonDisplayName(person) {
      if (typeof person === "string") return person;
      if (!person) return "";
      return person.name + (person.phone ? ` (${person.phone})` : "");
    },
    updateDate() {
      this.dateMenu = false;
      this.combineDateAndTime();
      if (!this.endDatePickerDate && this.timePickerEndTime) {
        this.updateEndTime();
      }
    },
    updateEndDate() {
      this.endDateMenu = false;
      this.updateEndTime();
    },
    updateTime() {
      this.combineDateAndTime();
    },
    updateEndTime() {
      const date = this.endDatePickerDate || this.datePickerDate;
      if (date && this.timePickerEndTime) {
        const dateTimeString = `${date}T${this.timePickerEndTime}`;
        this.cateringEvent.endDate = Timestamp.fromDate(
          new Date(dateTimeString),
        );
        this.saveEvent();
      }
    },
    async onMenuChange(val) {
      // val is the new value of cateringEvent.contentDescription (string because of return-object=false)
      const menuName = typeof val === "object" ? val.name : val;
      const menu = this.menus.find((m) => m.name === menuName);

      if (menu && menu.templateId) {
        const template = this.templates.find((t) => t.id === menu.templateId);
        if (template) {
          if (
            await this.$confirm(
              `Menuun "${menu.name}" on liitetty kauppalistapohja "${template.name}". Haluatko tuoda sen?`,
              { title: "Tuo kauppalista", buttonTrueText: "Tuo" },
            )
          ) {
            await this.applyTemplate(template);
          }
        }
      }
    },
    combineDateAndTime() {
      if (this.datePickerDate && this.timePickerTime) {
        const dateTimeString = `${this.datePickerDate}T${this.timePickerTime}`;
        this.cateringEvent.date = Timestamp.fromDate(new Date(dateTimeString));
        this.saveEvent();
      }
    },
    updateDefaultPrice() {
      const type = this.eventTypes.find(
        (t) => t.id === this.cateringEvent.type,
      );
      if (type) {
        this.cateringEvent.unitPrice = type.price;
        this.saveEvent();
      }
    },
    checkOrderReference() {
      if (this.cateringEvent.payer === "Pelastuslaitos") {
        //this.cateringEvent.orderReference = "T-63100P3";
      }
      this.saveEvent();
    },
    async saveEvent(showToast = true) {
      this.saving = true;
      const ref = doc(db, "caterings", this.cateringEvent.id);
      const data = { ...this.cateringEvent };
      delete data.id;

      // Audit fields
      if (!data.createdAt) {
        data.createdAt = Timestamp.now();
        data.createdBy = this.$userDBContext || null;
      }
      data.updatedAt = Timestamp.now();
      data.updatedBy = this.$userDBContext || null;

      // Update local state to reflect changes immediately (optional but good for UI)
      this.cateringEvent.updatedAt = data.updatedAt;
      this.cateringEvent.updatedBy = data.updatedBy;
      if (!this.cateringEvent.createdAt) {
        this.cateringEvent.createdAt = data.createdAt;
        this.cateringEvent.createdBy = data.createdBy;
      }

      await updateDoc(ref, data);
      this.saving = false;
      if (showToast) {
        this.$toast("Tallennettu", { type: "success" });
      }
    },
    async deleteEvent() {
      if (
        await this.$confirm("Haluatko varmasti poistaa tämän muonituksen?", {
          title: "Poista muonitus",
          color: "error",
          buttonTrueText: "Poista",
        })
      ) {
        const ref = doc(db, "caterings", this.cateringEvent.id);
        await updateDoc(ref, {
          deletedAt: Timestamp.now(),
          deletedBy: this.$userDBContext || null,
        });
        this.$router.push({ name: "Muonitus" });
        this.$toast("Muonitus poistettu", { type: "success" });
      }
    },
    saveEventDebounced: debounce(function () {
      this.saveEvent(false);
    }, 1000),
    async copyPageUrl() {
      const url = window.location.href;
      if (navigator.share) {
        try {
          await navigator.share({
            title: this.cateringEvent.description || "Muonitus",
            url: url,
          });
        } catch (e) {
          // Ignore aborts
          if (e.name !== "AbortError") {
            navigator.clipboard.writeText(url);
            this.$toast("Linkki kopioitu leikepöydälle!", { type: "success" });
          }
        }
      } else {
        navigator.clipboard.writeText(url);
        this.$toast("Linkki kopioitu leikepöydälle!", { type: "success" });
      }
    },
    async shareShoppingList() {
      this.cateringEvent.publicAccess = true;
      await this.saveEvent(false);
      const url = `${window.location.origin}/#/kauppalista/${this.cateringEvent.id}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: `Kauppalista: ${this.cateringEvent.description || ""}`,
            url: url,
          });
        } catch (e) {
          if (e.name !== "AbortError") {
            navigator.clipboard.writeText(url);
            this.$toast("Linkki julkiseen kauppalistaan kopioitu!", {
              type: "success",
            });
          }
        }
      } else {
        navigator.clipboard.writeText(url);
        this.$toast("Linkki julkiseen kauppalistaan kopioitu!", {
          type: "success",
        });
      }
    },
    async applyTemplate(template) {
      if (
        !(await this.$confirm(
          "Tämä korvaa nykyisen kauppalistan. Oletko varma?",
          { title: "Korvaa kauppalista", buttonTrueText: "Kyllä" },
        ))
      )
        return;

      const newList = template.items.map((item) => {
        let amount = 0;
        if (item.fixed) {
          amount = item.factor;
        } else {
          amount = Math.ceil(
            item.factor * (this.cateringEvent.headCountEstimate || 0),
          );
        }
        return {
          name: item.name,
          amount: amount,
          unit: item.unit,
          collected: false,
          factor: item.factor,
          fixed: item.fixed,
          category: item.category || "Muu",
          imageUrl: item.imageUrl || "",
          comment: item.comment || "",
        };
      });
      this.cateringEvent.shoppingList = newList;
      this.saveEvent();
    },
    updateShoppingListAmounts() {
      this.cateringEvent.shoppingList.forEach((item) => {
        if (!item.fixed && item.factor) {
          item.amount = Math.ceil(
            item.factor * (this.cateringEvent.headCountEstimate || 0),
          );
        }
      });
      this.saveEventDebounced();
    },
    copyStatusToClipboard() {
      const text =
        `${this.cateringEvent.description} (${this.formattedDate})\n` +
        `Tila: ${
          this.statusOptions.find((s) => s.value === this.cateringEvent.status)
            ?.text || this.cateringEvent.status
        }\n` +
        `Arvio: ${this.cateringEvent.headCountEstimate} hlö\n` +
        `Toteutuma: ${this.cateringEvent.headCountReal} hlö`;
      navigator.clipboard.writeText(text);
      this.$toast("Tilanne kopioitu leikepöydälle!", { type: "success" });
    },
    copyInvoiceData() {
      const text =
        `Tuote: Hälytysmuonitus kp50\n` +
        `Määrä: ${this.cateringEvent.headCountEstimate || 0} kpl\n` +
        `Yksikköhinta: ${(this.cateringEvent.unitPrice || 0).toFixed(2)} €\n` +
        `Yhteensä: ${this.billingTotal.toFixed(2)} €`;
      navigator.clipboard.writeText(text);
      this.$toast("Laskutustiedot kopioitu leikepöydälle!", {
        type: "success",
      });
    },
    async uploadReceiptFromComponent() {
      const files = await this.$refs.receiptUpload.getRawFiles();
      if (files.length === 0) {
        this.$toast("Valitse ensin kuva", { type: "warning" });
        return;
      }

      this.uploadingReceipt = true;
      try {
        for (const file of files) {
          await this.uploadReceipt(file);
        }
        this.$toast("Kuitit tallennettu", { type: "success" });
        if (this.$refs.receiptUpload) {
          this.$refs.receiptUpload.reset();
        }
      } catch (e) {
        console.error("Error uploading receipts:", e);
        this.$toast("Virhe kuitin latauksessa: " + e.message, {
          type: "error",
        });
      } finally {
        this.uploadingReceipt = false;
      }
    },
    async uploadReceipt(file) {
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        throw new Error(`Tiedosto ${file.name} ei ole kuva`);
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error(`Kuva ${file.name} on liian suuri (max 10MB)`);
      }

      // Process image (resize/compress)
      const processedFile = await this.$createPicture(file);

      const storageRef = ref(
        storage,
        `catering-receipts/${this.cateringEvent.id}/${file.name}`,
      );
      await uploadBytes(storageRef, processedFile);
      const url = await getDownloadURL(storageRef);

      if (!this.cateringEvent.receiptImageUrls)
        this.cateringEvent.receiptImageUrls = [];
      this.cateringEvent.receiptImageUrls.push(url);

      // Update legacy field for backward compatibility if it's the first image
      if (!this.cateringEvent.receiptImageUrl) {
        this.cateringEvent.receiptImageUrl = url;
      }

      await this.saveEvent();
    },
    async deleteReceipt(urlToDelete) {
      if (
        !(await this.$confirm("Haluatko varmasti poistaa kuitin?", {
          title: "Poista kuitti",
          color: "error",
          buttonTrueText: "Poista",
        }))
      )
        return;

      if (this.cateringEvent.receiptImageUrls) {
        const index = this.cateringEvent.receiptImageUrls.indexOf(urlToDelete);
        if (index > -1) {
          this.cateringEvent.receiptImageUrls.splice(index, 1);
        }
      }

      // Update legacy field
      if (this.cateringEvent.receiptImageUrl === urlToDelete) {
        this.cateringEvent.receiptImageUrl =
          this.cateringEvent.receiptImageUrls.length > 0
            ? this.cateringEvent.receiptImageUrls[0]
            : null;
      }

      await this.saveEvent();
      this.$toast("Kuitti poistettu", { type: "success" });
    },
    async printPdf() {
      // Resolve group member names
      const resolvedEvent = { ...this.cateringEvent };
      if (resolvedEvent.groupMembers && resolvedEvent.groupMembers.length > 0) {
        resolvedEvent.groupMembers = resolvedEvent.groupMembers.map((id) => {
          const person = this.supportPersonnel.find((p) => p.id === id);
          return person ? person.name : id;
        });
      }

      // Resolve Type Name
      const typeObj = this.eventTypes.find(
        (t) => t.id === this.cateringEvent.type,
      );
      const typeName = typeObj ? typeObj.name : this.cateringEvent.type || "-";

      await generateMuonitusPdf(
        resolvedEvent,
        this.checklistItems,
        this.formattedDate,
        this.timePickerTime,
        this.timePickerEndTime,
        this.selectedMenuDescription,
        typeName,
        this.selectedCostAllocations,
        this.formattedEndDate,
      );
    },
  },
};
</script>

<style lang="scss">
.muonitus-tapahtuma .v-slide-group__prev.v-slide-group__prev--disabled {
  display: none !important;
}
</style>
